import React, { useContext, useEffect } from 'react';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router-dom';
import { Button, FormGroup, Heading, Select, Col } from '@snsw-gel/react';
import FlowStateContext from 'app/contexts/FlowStateContext';
import { useEventFlow } from 'app/contexts/EventFlowContext';
import BusinessSelect, { BusinessSearchResult } from 'app/components/common/businessSelect/BusinessSelect';
import { MultiSelectField } from 'app/components/common/multiSelectField/MultiSelectField';
import {
  FieldWrapper,
  FormContainer,
  StyledBackButton,
  StyledRow,
  StyledHeadingWrapper,
} from './EventBusinessDetails.styled';
import EventLayout from './EventLayout';

interface EventBusinessDetailsProps {
  agencyName: string;
}

interface BusinessDetailsForm {
  businessName: string;
  identifier: string;
  identifierType: string;
  businessAddress: string;
  businessStage: string;
  role: string;
  numberOfEmployees: string;
  businessIndustry: string;
  businessIdentity: Array<{ value: string; label: string }>;
}

const BUSINESS_STAGE_OPTIONS = [
  "I'm thinking about starting a business",
  "I'm starting a business",
  'My business is operating and less than 2 years old',
  'My business is operating and 2 or more years old',
  "I'm looking to grow or expand my business",
  "I'm planning to close my business",
].map((val) => ({ value: val, text: val }));

const BUSINESS_ROLE_OPTIONS = ['Owner/Director/Part owner', 'Staff/employee'].map((val) => ({
  value: val,
  text: val,
}));

const EMPLOYEE_COUNT_OPTIONS = ['Just me', '2-5', '6-10', '11-19', '20 or more'].map((val) => ({
  value: val,
  text: val,
}));

const BUSINESS_IDENTITY_OPTIONS = [
  'Aboriginal business',
  'Torres Strait Islander business',
  'Multicultural business',
  'Prefer not to answer',
].map((val) => ({ value: val, label: val }));

const BUSINESS_INDUSTRY_OPTIONS = [
  'Accommodation',
  'Administrative and Support Services',
  'Advertising Services',
  'Agriculture, Forestry and Fishing',
  'Architectural, Engineering and Technical Services',
  'Arts and Recreation Services',
  'Café and restaurants',
  'Club (hospitality)',
  'Computer System Design and Related Services',
  'Construction',
  'Education and Training',
  'Electricity, Gas, Water and Waste Services',
  'Financial and Insurance Services',
  'Health Care and Social Assistance',
  'Information Media and Telecommunications',
  'Legal and Accounting Services',
  'Licenced venue',
  'Management and Other Consulting Services',
  'Manufacturing',
  'Mining',
  'Rental, Hiring and Real Estate Services',
  'Retail Trade',
  'Transport, Postal and Warehousing',
  'Wholesale trade',
  'Other Services',
].map((val) => ({ value: val, text: val }));

const EventBusinessDetails: React.FC<EventBusinessDetailsProps> = ({ agencyName }) => {
  const history = useHistory();
  const location = useLocation<{ referrer?: string }>();
  const { formData } = useContext(FlowStateContext);
  const { businessDetails: savedDetails, setBusinessDetails, setPersonalDetails } = useEventFlow();

  const form = useForm<BusinessDetailsForm>({
    defaultValues: savedDetails || {
      businessName: '',
      identifier: '',
      identifierType: '',
      businessAddress: '',
      businessStage: '',
      role: '',
      numberOfEmployees: '',
      businessIndustry: '',
      businessIdentity: [],
    },
  });

  const {
    handleSubmit,
    setValue,
    watch,
    control,
    clearErrors,
    formState: { errors },
  } = form;

  // If arriving from Profile Connect success, save personal details to sessionStorage
  useEffect(() => {
    const cameFromProfileConnect = location.state?.referrer === '/profile-connect/success';
    if (cameFromProfileConnect && formData?.personalDetails) {
      const pd = formData.personalDetails;
      setPersonalDetails({
        firstName: pd.firstName || '',
        lastName: pd.lastName || '',
        email: pd.email || '',
        phone: pd.phone || '',
      });
    }
  }, []);

  const onSubmit = (data: BusinessDetailsForm) => {
    setBusinessDetails(data);
    history.push(`/events/${agencyName}/review`);
  };

  const onBack = () => {
    history.push(`/events/${agencyName}/details`);
  };

  const handleBusinessSelect = (business: BusinessSearchResult) => {
    setValue('businessName', business.businessName, { shouldValidate: true });
    setValue('identifier', business.identifier);
    setValue('identifierType', business.identifierType);
    setValue('businessAddress', business.businessAddress?.formattedAddress || '', { shouldValidate: true });
  };

  const handleBusinessClear = () => {
    setValue('businessName', '', { shouldValidate: true });
    setValue('identifier', '');
    setValue('identifierType', '');
    setValue('businessAddress', '', { shouldValidate: true });
  };

  return (
    <EventLayout currentStep={2}>
      <Heading level={1}>Business details</Heading>
      <p>You can still continue if you do not have a registered business or ABN.</p>
      <FormProvider {...form}>
        <FormContainer>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            {/* Business search */}
            <BusinessSelect
              onSelect={handleBusinessSelect}
              onClear={handleBusinessClear}
              onSearchChange={(value) => setValue('businessName', value)}
              businessName={watch('businessName')}
              businessAddress={watch('businessAddress')}
              onAddressChange={(address) =>
                setValue('businessAddress', address.formattedAddress.value, { shouldValidate: true })
              }
              businessNameError={errors.businessName?.message}
              businessAddressError={errors.businessAddress?.message}
              addressHelpMessage="Search the business address."
            />

            {/* Business Stage */}
            <Controller
              name="businessStage"
              control={control}
              rules={{ required: 'Please select a business stage' }}
              render={({ field }) => (
                <FormGroup
                  id="businessStage"
                  label="What stage is your business at?"
                  helpMessage="Select what best describes your business stage."
                  errorMessage={errors.businessStage?.message}
                  hasError={!!errors.businessStage}
                >
                  <Select
                    options={BUSINESS_STAGE_OPTIONS}
                    value={field.value}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => field.onChange(e.target.value)}
                    inputWidth="xl"
                  />
                </FormGroup>
              )}
            />

            {/* Business Role */}
            <Controller
              name="role"
              control={control}
              render={({ field }) => (
                <FormGroup
                  id="role"
                  label="What is your role in the business? (optional)"
                  errorMessage={errors.role?.message}
                  hasError={!!errors.role}
                >
                  <Select
                    options={BUSINESS_ROLE_OPTIONS}
                    value={field.value}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => field.onChange(e.target.value)}
                    inputWidth="xl"
                  />
                </FormGroup>
              )}
            />

            {/* Employee Count */}
            <Controller
              name="numberOfEmployees"
              control={control}
              rules={{ required: 'Please select the number of employees' }}
              render={({ field }) => (
                <FormGroup
                  id="numberOfEmployees"
                  label="How many people work in your business?"
                  errorMessage={errors.numberOfEmployees?.message}
                  hasError={!!errors.numberOfEmployees}
                >
                  <Select
                    options={EMPLOYEE_COUNT_OPTIONS}
                    value={field.value}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => field.onChange(e.target.value)}
                    inputWidth="xl"
                  />
                </FormGroup>
              )}
            />

            {/* Business identity */}
            <FieldWrapper>
              <MultiSelectField
                name="businessIdentity"
                id="businessIdentityMultiSelect"
                label="Does your business identify as any of the following? (optional)"
                helpMessage="Select all that apply."
                placeHolder="Select"
                options={BUSINESS_IDENTITY_OPTIONS}
                control={control}
                errors={errors}
                clearErrors={clearErrors}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                styles={{
                  control: (provided: Record<string, unknown>, state: { isFocused: boolean }) => ({
                    ...provided,
                    border: '2px solid #22272B',
                    borderRadius: '4px',
                    minHeight: '48px',
                    boxShadow: 'none',
                    outline: state.isFocused ? '3px solid #2E5299' : 'none',
                    outlineOffset: state.isFocused ? '3px' : 'none',
                  }),
                  option: (provided: Record<string, unknown>) => ({
                    ...provided,
                    textDecoration: 'none',
                  }),
                  dropdownIndicator: (provided: Record<string, unknown>) => ({
                    ...provided,
                    color: '#22272B',
                    svg: {
                      width: '20px',
                      height: '20px',
                    },
                  }),
                  indicatorSeparator: () => ({
                    display: 'none',
                  }),
                }}
              />
            </FieldWrapper>

            {/* Business industry */}
            <Controller
              name="businessIndustry"
              control={control}
              render={({ field }) => (
                <FormGroup id="businessIndustry" label="What industry does your business operate in? (optional)">
                  <Select
                    options={BUSINESS_INDUSTRY_OPTIONS}
                    value={field.value}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => field.onChange(e.target.value)}
                    inputWidth="xl"
                  />
                </FormGroup>
              )}
            />

            <StyledRow>
              <Col>
                <Button type="submit">Continue</Button>
              </Col>
            </StyledRow>

            <StyledRow>
              <Col>
                <StyledBackButton variant="back" onClick={onBack} type="button">
                  Back
                </StyledBackButton>
              </Col>
            </StyledRow>
          </form>
        </FormContainer>
      </FormProvider>
    </EventLayout>
  );
};

export default EventBusinessDetails;
